import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Search, X } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import MasterTable from "./MasterTable";
import { getSchools, createSchool, updateSchool, toggleSchoolActive, getMasterDropdownService, getCitiesBasedOnState } from "@/services/masters";
import AddSchoolForm, { SchoolFormData } from "./schools/AddSchool";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import MasterTableHeader from "./generics/MasterTableHeader";

interface School {
  id?: string | number;
  name: string;
  address: string;
  state: string;
  city: string;
  branch?: string;
  School_Branch?: string;
  latitude: string;
  longitude: string;
  isActive: boolean;
  Status?: string;
}

export default function Schools() {
  const { canCreate, canUpdate } = useScreenPermission("schools");
  const { isZoneManager, isBranchIncharge, stateCode, cityCode } = useRoleContext();
  const [schools, setSchools] = useState<School[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [pendingCity, setPendingCity] = useState("");
  const [togglingId, setTogglingId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<"" | "Approved" | "Pending">("");
  const [localSchools, setLocalSchools] = useState<School[]>([]);
  const [activeFilter, setActiveFilter] = useState("");

  const methods = useForm<SchoolFormData & { Status?: string }>({
    defaultValues: {
      School_Name: "", Address: "", State: "", City: "",
      School_Branch: "", Latitude: "", Longitude: "",
      Status: "Pending",
    }
  });

  const loadSchools = useCallback(async (p: number = 1, search: string = "", selectedStatus: string = statusFilter, selectedActive: string = activeFilter) => {
    setIsLoading(true);
    try {
      const [{ data, status }, masterRes] = await Promise.all([
        getSchools(p, search, (isZoneManager || isBranchIncharge) ? stateCode : undefined, (isZoneManager || isBranchIncharge) ? cityCode : undefined, selectedStatus === "" ? "" : selectedStatus, selectedActive === "" ? "" : selectedActive),
        getMasterDropdownService(),
      ]);
      if (status === 200 && data) {
        const d = data as any;
        const items: any[] = d.results ?? (Array.isArray(d) ? d : []);

        // Build state code→name and name→code maps
        const rawStates: any[] = masterRes.status === 200 ? (masterRes.data as any)?.States || [] : [];
        const stateMap = new Map(rawStates.map((s: any) => [s.unique_code, s.Name]));
        const stateNameToCode = new Map(rawStates.map((s: any) => [s.Name, s.unique_code]));

        // Batch-load cities — resolve state value to unique_code first
        const uniqueStateValues = [...new Set(items.map((item: any) => item.State).filter(Boolean))];
        const cityMap = new Map<string, string>();
        await Promise.all(uniqueStateValues.map(async (val) => {
          const stateCode = stateNameToCode.get(val as string) || val as string;
          try {
            const res = await getCitiesBasedOnState(stateCode);
            const cities: any[] = (res.data as any)?.Cities || [];
            cities.forEach((c: any) => {
              if (typeof c === "object" && c.unique_code) cityMap.set(c.unique_code, c.City_Name);
              if (typeof c === "object" && c.City_Name) cityMap.set(c.City_Name, c.City_Name);
            });
          } catch { }
        }));

        const mappedSchools = items.map((item: any) => ({
          id: item.id,
          name: item.School_Name,
          address: item.Address,
          state: stateMap.get(item.State) || item.State,
          city: cityMap.get(item.City) || item.City,
          School_Branch: item.School_Branch || "",
          latitude: item.Latitude,
          longitude: item.Longitude,
          isActive: item.Is_Active ?? true,
          Status: item.Status || "",
        }));

        setSchools(mappedSchools);
        setLocalSchools(mappedSchools);

        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count)
      } else {
        // toast.error("Failed to load schools");
      }
    } catch (error) {
      toast.error("Error loading schools");
    } finally {
      setIsLoading(false);
    }
  }, [isZoneManager, isBranchIncharge, stateCode, cityCode, statusFilter,activeFilter]);

  // Initial load + debounced search
  useEffect(() => {
    const delay = searchQuery ? 400 : 0;
    const timer = setTimeout(() => {
      setPage(1);
      loadSchools(1, searchQuery);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, loadSchools, statusFilter, activeFilter]);

  const handleOpenModal = useCallback(async (school?: School) => {
    if (school) {
      const foundSchool = schools.find(s => s.id === school.id);
      if (foundSchool) {
        setPendingCity(foundSchool.city);
        methods.reset({
          School_Name: foundSchool.name, Address: foundSchool.address,
          State: foundSchool.state, City: "",
          School_Branch: foundSchool.branch || "", Latitude: foundSchool.latitude,
          Longitude: foundSchool.longitude, Status: foundSchool.Status || "Pending",
        });
        setEditingSchool(foundSchool);
      }
    } else {
      setPendingCity("");
      methods.reset({ School_Name: "", Address: "", State: "", City: "", School_Branch: "", Latitude: "", Longitude: "" });
      setEditingSchool(null);
    }
    setIsModalOpen(true);
  }, [schools, methods]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingSchool(null);
    methods.reset();
  }, [methods]);

  const handleSubmit = useCallback(
    async (data: SchoolFormData) => {
      methods.clearErrors();
      setIsLoading(true);

      try {
        const {
          School_Branch,
          ...rest
        } = data;

        const payload = {
          ...rest,
          School_Branch,
        };

        const response = editingSchool?.id
          ? await updateSchool(
            Number(editingSchool.id),
            payload
          )
          : await createSchool(payload);

        if (
          response.status === 200 ||
          response.status === 201
        ) {
          toast.success(
            editingSchool
              ? "School updated successfully"
              : "School created successfully"
          );

          handleCloseModal();

          await loadSchools(
            page,
            searchQuery
          );
        } else if (
          response.errors &&
          typeof response.errors === "object"
        ) {
          let hasFieldErrors =
            false;

          Object.entries(
            response.errors
          ).forEach(
            ([key, value]) => {
              const msg =
                Array.isArray(
                  value
                )
                  ? value[0]
                  : String(value);

              if (
                key === "error" ||
                key ===
                "non_field_errors" ||
                key === "detail"
              ) {
                toast.error(msg);
              } else {
                methods.setError(
                  key as keyof SchoolFormData,
                  {
                    type:
                      "server",
                    message: msg,
                  }
                );

                hasFieldErrors =
                  true;
              }
            }
          );

          if (!hasFieldErrors) {
            toast.error(
              "Failed to save school"
            );
          }
        } else {
          toast.error(
            "Failed to save school"
          );
        }
      } catch (error) {
        toast.error(
          "Error saving school"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      editingSchool,
      handleCloseModal,
      loadSchools,
      methods,
      page,
      searchQuery,
    ]
  );

  const handleToggleActive = useCallback(async (school: School) => {
    if (!school.id || togglingId === school.id) return;

    const newStatus = !school.isActive;

    // Optimistic update
    setLocalSchools(prev =>
      prev.map(s =>
        s.id === school.id
          ? { ...s, isActive: newStatus }
          : s
      )
    );

    setTogglingId(school.id);

    try {
      const response = await toggleSchoolActive(Number(school.id), {
        Is_Active: newStatus,
      });

      if (response.status === 200) {
        toast.success(
          `School ${newStatus ? "activated" : "deactivated"} successfully`
        );
        loadSchools(page, searchQuery, statusFilter, activeFilter)
      } else {
        // rollback
        setLocalSchools(prev =>
          prev.map(s =>
            s.id === school.id
              ? { ...s, isActive: school.isActive }
              : s
          )
        );
        toast.error("Failed to update school status");
      }
    } catch {
      // rollback
      setLocalSchools(prev =>
        prev.map(s =>
          s.id === school.id
            ? { ...s, isActive: school.isActive }
            : s
        )
      );
      toast.error("Error updating school status");
    } finally {
      setTogglingId(null);
    }
  }, [togglingId]);

  const columns = useMemo(
    () => [
      { key: "name" as const, label: "School Name" },
      { key: "state" as const, label: "State" },
      { key: "city" as const, label: "District" },
      { key: "School_Branch" as const, label: "School Branch" },
      { key: "address" as const, label: "Address" },
      { key: "latitude" as const, label: "Latitude" },
      { key: "longitude" as const, label: "Longitude" },
      {
        key: "Status" as const,
        label: "Approval Status",
      },
    ],
    [handleToggleActive, togglingId]
  );

  return (
    <div className="space-y-6">
      <MasterTableHeader
  title="Schools Management"
  description="Create, edit, and manage schools"
  canCreate={canCreate}
  addButtonText="Add New School"
  onAdd={() => handleOpenModal()}
  searchPlaceholder="Search by name, state, city or branch..."
  searchQuery={searchQuery}
  setSearchQuery={setSearchQuery}
  showDropdownFilter
  dropdownValue={activeFilter}
  onDropdownChange={(value) => {
    setActiveFilter(value);
    loadSchools(1, searchQuery, statusFilter, value);
  }}

  showStatusFilter
  statusFilter={statusFilter}
  statusOptions={[
    {
      label: "Approved",
      value: "Approved",
    },
    {
      label: "Pending",
      value: "Pending",
    },
  ]}
  onStatusChange={(value) => {
    setStatusFilter(value as "Approved" | "Pending" | "");
    setPage(1);
    loadSchools(1, searchQuery, value, activeFilter);
  }}
  showClearFilters
  onClearFilters={() => {
    setSearchQuery("");
    setActiveFilter("");
    setStatusFilter("");
    setPage(1);

    loadSchools(1, "", "", "");
  }}
/>
      <MasterTable
        columns={columns}
        data={localSchools}
        onEdit={handleOpenModal}
        onToggleActive={handleToggleActive}
        togglingId={togglingId}
        isLoading={isLoading && schools.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadSchools(p, searchQuery); }}
        canEdit={canUpdate}
        canToggle={canUpdate}
        totalCount={totalCount}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingSchool ? "Edit School" : "Add New School"}
        onClose={handleCloseModal}
        onSubmit={methods.handleSubmit(handleSubmit)}
        isLoading={isLoading}
      >
        <FormProvider {...methods}>
          <AddSchoolForm
            pendingCity={pendingCity}
            isEdit={!!editingSchool}
            currentStatus={
              editingSchool?.Status as
              | "Pending"
              | "Approved"
            }
          />
        </FormProvider>
      </MasterModal>
    </div>
  );
}
